import React from 'react';
import { MapPin, Phone, Fan as Fax, Mail } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const Branches: React.FC<{ content?: any }> = ({ content }) => {
  const { t_api } = useLanguage();

  const defaultBranches = [
    { name: t_api("Muscat (Ghala) HQ", "مسقط (غلا) المقر الرئيسي"), phone: "+(968) 24-595058", fax: "+(968) 24-501818", email: "Ghala.Dunlop@hamdanoman.com", coordinates: "23.575480, 58.354941" },
    { name: t_api("Salalah (New Salalah)", "صلالة (صلالة الجديدة)"), phone: "+(968) 23-291561", fax: "+(968) 232-97035", email: "Salalah.Dunlop@hamdanoman.com", coordinates: "17.021328, 54.117397" },
    { name: t_api("Ibri", "عبري"), phone: "+(968) 25-691242", fax: "+(968) 25-691242", email: "Ibri.Dunlop@hamdanoman.com", coordinates: "23.215939, 56.512076" },
    { name: t_api("Rustaq", "الرستاق"), phone: "+(968) 9910825", fax: "", email: "Rustaq.Dunlop@hamdanoman.com", coordinates: "23.434510, 57.391902" },
    { name: t_api("Sinaw", "سناو"), phone: "+(968) 23-436134", fax: "+(968) 23-436134", email: "Sinaw.Dunlop@hamdanoman.com", coordinates: "22.505329, 58.034167" },
    { name: t_api("Sohar", "صحار"), phone: "+(968) 26-846433", fax: "+(968) 26-846866", email: "Sohar.Dunlop@hamdanoman.com", coordinates: "24.343390, 56.697076" },
    { name: t_api("Muscat (Al Wadi Al Kabir)", "مسقط (الوادي الكبير)"), phone: "+(968) 24-811458", fax: "+(968) 24-814380", email: "Wadikabir.Dunlop@hamdanoman.com", coordinates: "23.587644, 58.554047" },
    { name: t_api("Barka", "بركاء"), phone: "+(968) 26-884250", fax: "+(968) 26-884235", email: "Barka.Dunlop@hamdanoman.com", coordinates: "23.667609, 57.889009" },
    { name: t_api("Suwayq", "السويق"), phone: "+(968) 99108262", fax: "", email: "Suwayq.Dunlop@hamdanoman.com", coordinates: "23.902097, 57.220016" },
    { name: t_api("Haima", "هيماء"), phone: "+(968) 23-436134", fax: "+(968) 23-436134", email: "Haima.Dunlop@hamdanoman.com", coordinates: "19.950266, 56.264675" },
    { name: t_api("Buraimi", "البريمي"), phone: "+(968) 25-651510", fax: "+(968) 25-651510", email: "Buraimi.Dunlop@hamdanoman.com", coordinates: "24.255326, 55.769307" },
    { name: t_api("Sur", "صور"), phone: "+(968) 25-541017", fax: "+(968) 25-541017", email: "Sur.Dunlop@hamdanoman.com", coordinates: "22.568787, 59.497667" },
  ];

  const defaultStats = [
    { value: "12", label: t_api("Total Branches", "إجمالي الفروع") },
    { value: "3", label: t_api("Major Cities", "المدن الرئيسية") },
    { value: "9", label: t_api("Regional Areas", "المناطق الإقليمية") },
    { value: "40+", label: t_api("Years Experience", "سنوات الخبرة") },
  ];

  const statColors = ["blue", "green", "yellow", "purple"];

  const branches = (content?.branches?.length > 0 ? content.branches : defaultBranches);
  const networkTitle = content?.network_title || t_api("Our Network Coverage", "تغطية شبكتنا");
  const networkDesc = content?.network_desc || t_api("With 12 strategically located branches across the Sultanate of Oman, we ensure comprehensive coverage from major cities to remote areas, providing easy access to quality Dunlop tyres and services.", "مع 12 فرعًا بمواقع استراتيجية في جميع أنحاء سلطنة عمان، نضمن تغطية شاملة من المدن الرئيسية إلى المناطق النائية، مما يوفر سهولة الوصول إلى إطارات وخدمات دنلوب عالية الجودة.");
  const stats = content?.stats?.filter((s: any) => s.value)?.length > 0
    ? content.stats
    : defaultStats;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg border p-8">
        {/* Branch Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {branches.map((branch: any, index: number) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white"
            >
              <div className="bg-gray-200 px-4 py-3 flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-gray-700" />
                <h3 className="text-base font-semibold text-gray-900">{branch.name}</h3>
              </div>
              <div className="p-6 space-y-2 text-sm bg-white">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <span className="text-gray-700 text-[12px] md:text-[14px]">Tel: {branch.phone}</span>
                </div>
                {branch.fax && (
                  <div className="flex items-center space-x-2">
                    <Fax className="h-4 w-4 text-green-600" />
                    <span className="text-gray-700 text-[12px] md:text-[14px]">Fax: {branch.fax}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-red-600" />
                  <span className="text-gray-700 text-[12px] md:text-[14px]">Email: {branch.email}</span>
                </div>
                {branch.coordinates && (
                  <div className="flex items-start space-x-2">
                    <MapPin className="h-4 w-4 text-purple-600 mt-0.5" />
                    <span className="text-gray-700 text-[12px] md:text-[14px]">Coordinates: {branch.coordinates}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Network Summary */}
        <div
          className="mt-8 bg-gray-50 p-6 rounded-lg"
        >
          <h3 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-6">{networkTitle}</h3>
          <p className="text-gray-700 text-[15px] md:text-base mb-8 text-center max-w-6xl mx-auto">{networkDesc}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {stats.map((stat: any, i: number) => (
              <div
                key={i}
                className={`bg-${statColors[i]}-100 p-3 rounded`}
              >
                <div className={`text-2xl font-bold text-${statColors[i]}-600`}>{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Branches;
